import type { MapBoxGL } from '../../MapBoxGL'
import type { EventData, MapLayerMouseEvent, MapLayerTouchEvent } from 'mapbox-gl'

/**
 * Event handler entry tracking layer ID and handler function.
 */
interface EventHandlerEntry {
  layerId: string
  handler: (ev: (MapLayerMouseEvent | MapLayerTouchEvent) & EventData) => void
}

/**
 * Manages MapBox layer event handlers with proper cleanup.
 *
 * Solves the problem of duplicate event handlers being registered
 * when layers are reloaded. Automatically unregisters old handlers
 * before registering new ones.
 *
 * This eliminates the need for counter hacks or manual tracking.
 */
export class MarkerEventManager {
  private handlers: Map<string, EventHandlerEntry> = new Map()

  /**
   * Register a click event handler for a layer.
   *
   * If a handler is already registered for this layer, it will be
   * automatically unregistered first.
   *
   * @param mapbox - MapBox GL instance
   * @param layerId - ID of the layer to attach the handler to
   * @param handler - Click event handler function
   */
  registerClickHandler(mapbox: MapBoxGL, layerId: string, handler: (ev: (MapLayerMouseEvent | MapLayerTouchEvent) & EventData) => void): void {
    // Unregister existing handler if present
    this.unregisterClickHandler(mapbox, layerId)

    // Register new handler
    mapbox.addLayerEventHandler('click', layerId, handler)
    this.handlers.set(layerId, { layerId, handler })
  }

  /**
   * Unregister a click event handler for a specific layer.
   *
   * @param mapbox - MapBox GL instance
   * @param layerId - ID of the layer to remove the handler from
   */
  unregisterClickHandler(mapbox: MapBoxGL, layerId: string): void {
    const entry = this.handlers.get(layerId)
    if (entry) {
      mapbox.removeLayerEventHandler('click', entry.layerId, entry.handler)
      this.handlers.delete(layerId)
    }
  }

  /**
   * Unregister all click event handlers.
   *
   * Useful for cleanup when controller is destroyed or when
   * completely reloading all layers.
   *
   * @param mapbox - MapBox GL instance
   */
  unregisterAllClickHandlers(mapbox: MapBoxGL): void {
    for (const entry of this.handlers.values()) {
      mapbox.removeLayerEventHandler('click', entry.layerId, entry.handler)
    }
    this.handlers.clear()
  }

  /**
   * Get the number of registered handlers.
   *
   * @returns Count of active handlers
   */
  get handlerCount(): number {
    return this.handlers.size
  }
}
